import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Spinner,
} from "@empite/ui-components";
import type {
  Operator,
  ProviderAccountStatus,
  ProviderAccountSummary,
  ProviderCredential,
  ProviderProfileRef,
} from "../admin-client";
import {
  useProviderAccount,
  useRevokeProviderCredential,
  useRotateProviderCredential,
  useSetProviderAccountStatus,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { FlagBadge, HealthBadge, StatusBadge, microsLimit, when } from "./common";

/** The two permissions the Admin API guards this page's writes with. The
 *  strings are the server's; they are repeated here only to decide what to
 *  draw. Hiding a control is a courtesy — the route re-checks and audits the
 *  refusal — and that is true of every `can` call in this portal. */
const WRITE = "providers:write";
const ROTATE = "provider_credentials:rotate";

/** Enough of a fingerprint to compare two rows by eye, not so much that the
 *  column stops being readable. The full value is on the element's title. */
const FINGERPRINT_CHARS = 12;

/**
 * One provider account: what it is, what keys it runs on, and who routes
 * through it.
 *
 * The credentials table is the point of the page, and it is deliberately a
 * metadata table. Last four, a fingerprint and three timestamps are what an
 * operator needs to answer "is this the key the provider's console shows",
 * "has anything used it", and "when was it last changed" — none of which
 * require anyone to see the key.
 */
export function ProviderDetail({ accountId }: { accountId: string }) {
  return (
    <RequireOperator>
      {(session) => <ProviderPanels accountId={accountId} operator={session.operator} />}
    </RequireOperator>
  );
}

function ProviderPanels({ accountId, operator }: { accountId: string; operator: Operator }) {
  const account = useProviderAccount(accountId);
  // One banner for the page: the cards below all mutate, and an operator who
  // rotated a key at the bottom still has to see that it worked.
  const [notice, setNotice] = useState<string | null>(null);

  if (account.isPending) {
    return <Spinner label="Loading the provider account" />;
  }
  if (account.error || !account.data) {
    return <ErrorAlert error={account.error} title="Could not load the provider account" />;
  }

  const { provider_account: acct, credentials, routing_profiles: profiles } = account.data;

  return (
    <>
      <PageHeader
        title={acct.label || acct.provider}
        description={
          <>
            <code>{acct.provider}</code> in {acct.environment} ·{" "}
            <Link to="/providers">back to all providers</Link>
          </>
        }
        actions={
          <>
            <StatusBadge status={acct.status} />
            <HealthBadge health={acct.health} />
          </>
        }
      />
      {notice ? <Alert tone="success" title={notice} /> : null}
      <AccountCard account={acct} operator={operator} onDone={setNotice} />
      <CredentialsCard
        accountId={accountId}
        credentials={credentials}
        operator={operator}
        onDone={setNotice}
      />
      <ProfilesCard profiles={profiles} />
    </>
  );
}

/** The status changes offered from wherever the account is now. Suspend and
 *  disable both stop traffic; the distinction the server draws is temporary
 *  versus retired, so the descriptions say which is which rather than leaving
 *  an operator to guess from two red buttons. */
const STATUS_ACTIONS: Array<{
  status: ProviderAccountStatus;
  label: string;
  danger: boolean;
  description: string;
}> = [
  {
    status: "active",
    label: "Enable account",
    danger: false,
    description: "Routing may send traffic to this account again, on its active credentials.",
  },
  {
    status: "suspended",
    label: "Suspend account",
    danger: true,
    description:
      "Routing stops using this account immediately. Its credentials are left alone, so a suspension can be lifted without touching any key.",
  },
  {
    status: "disabled",
    label: "Disable account",
    danger: true,
    description:
      "Routing stops using this account and it is treated as retired. Use suspend for anything you expect to undo today.",
  },
];

function AccountCard({
  account,
  operator,
  onDone,
}: {
  account: ProviderAccountSummary;
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const setStatus = useSetProviderAccountStatus();

  return (
    <Card title="Account">
      <ErrorAlert error={setStatus.error} title="That status change failed" />
      <dl className="esm-detail-list">
        <dt>Identifier</dt>
        <dd>
          <code>{account.id}</code>
        </dd>
        <dt>Provider</dt>
        <dd>
          <code>{account.provider}</code>
        </dd>
        <dt>Environment</dt>
        <dd>
          <Badge tone={account.environment === "prod" ? "warning" : "neutral"}>
            {account.environment}
          </Badge>
        </dd>
        <dt>Label</dt>
        <dd>{account.label || <span className="esm-muted-text">unlabelled</span>}</dd>
        <dt>Region</dt>
        <dd>
          {account.region ? (
            <code>{account.region}</code>
          ) : (
            <span className="esm-muted-text">—</span>
          )}
        </dd>
        <dt>Base URL</dt>
        <dd>
          {/* Text, never a link. This is a value an operator is auditing, and
              a console that offers to navigate to whatever was configured is
              a console that can be pointed at somebody else's endpoint. */}
          {account.base_url ? (
            <code>{account.base_url}</code>
          ) : (
            <span className="esm-muted-text">the provider's default</span>
          )}
        </dd>
        <dt>Monthly budget</dt>
        <dd>{microsLimit(account.monthly_budget_micros)}</dd>
        <dt>Rate limit</dt>
        <dd>
          {account.rate_limit_rpm === null
            ? "no limit"
            : `${account.rate_limit_rpm.toLocaleString()} requests/minute`}
        </dd>
        <dt>Health</dt>
        <dd>
          <HealthBadge health={account.health} />
        </dd>
        <dt>Last error</dt>
        {/* The absence of a recent error is itself the answer, so the row
            stays and says so rather than disappearing. */}
        <dd>{account.last_error_at ? when(account.last_error_at) : "none reported"}</dd>
        <dt>Registered</dt>
        <dd>{when(account.created_at)}</dd>
        <dt>Updated</dt>
        <dd>{when(account.updated_at)}</dd>
      </dl>
      <div className="esm-actions">
        {can(operator, WRITE) ? (
          STATUS_ACTIONS.filter((action) => action.status !== account.status).map((action) => (
            <ReasonButton
              key={action.status}
              variant={action.danger ? "danger" : "primary"}
              busy={setStatus.isPending && setStatus.variables?.status === action.status}
              mutation={{
                title: `${action.label}: ${account.label || account.provider}`,
                description: <p>{action.description}</p>,
                confirmLabel: action.label,
                run: async (reason) => {
                  const result = await setStatus.mutateAsync({
                    accountID: account.id,
                    status: action.status,
                    reason,
                  });
                  onDone(`${account.label || account.provider} is now ${result.status}.`);
                },
              }}
            >
              {action.label}
            </ReasonButton>
          ))
        ) : (
          <span className="esm-muted-text">
            Your roles do not grant <code>{WRITE}</code>.
          </span>
        )}
      </div>
    </Card>
  );
}

function CredentialsCard({
  accountId,
  credentials,
  operator,
  onDone,
}: {
  accountId: string;
  credentials: ProviderCredential[];
  operator: Operator;
  onDone: (message: string) => void;
}) {
  const revoke = useRevokeProviderCredential();
  const rotatable = can(operator, ROTATE);
  // Which credential the rotate form is open for. One at a time, and held
  // here rather than in each row so opening a second closes the first — two
  // password boxes on one page is two chances to paste a key into the wrong
  // one.
  const [rotating, setRotating] = useState<string | null>(null);
  const target = credentials.find((credential) => credential.id === rotating);

  return (
    <Card title="Credentials">
      <Alert tone="info" title="This console cannot show you an existing key">
        There is no operation that reveals one — not here and not in the Admin API. Rotation
        replaces a key with one you supply; it never reads the old one back.
      </Alert>
      <ErrorAlert error={revoke.error} title="That credential could not be revoked" />
      {rotatable ? null : (
        <p className="esm-muted-text">
          Your roles do not grant <code>{ROTATE}</code>, so this table is read-only for you.
        </p>
      )}
      {/* Revoked credentials stay in the table, marked. A list that drops
          what it revoked cannot answer "was this already revoked when I got
          here?", which is the first question of every key-leak postmortem. */}
      <DataTable<ProviderCredential>
        rows={credentials}
        rowKey={(row) => row.id}
        empty="This account has no credential, so nothing can route to it."
        columns={[
          {
            key: "label",
            header: "Credential",
            render: (row) => (
              <>
                <div>{row.label || "unlabelled"}</div>
                <code className="esm-muted-text">{row.id}</code>
              </>
            ),
          },
          {
            key: "status",
            header: "Status",
            render: (row) => <StatusBadge status={row.status} />,
          },
          {
            key: "validation",
            header: "Validation",
            render: (row) => <StatusBadge status={row.validation_state} />,
          },
          {
            key: "last_four",
            header: "Key",
            // The only rendering of a key anywhere in this portal: four
            // characters the provider's own console also shows.
            render: (row) => <code>••••{row.last_four}</code>,
          },
          {
            key: "fingerprint",
            header: "Fingerprint",
            render: (row) => (
              <code title={row.fingerprint}>{row.fingerprint.slice(0, FINGERPRINT_CHARS)}</code>
            ),
          },
          { key: "priority", header: "Priority", align: "end", render: (row) => row.priority },
          { key: "created", header: "Created", render: (row) => when(row.created_at) },
          { key: "rotated", header: "Rotated", render: (row) => when(row.rotated_at) },
          { key: "used", header: "Last used", render: (row) => when(row.last_used_at) },
          {
            key: "actions",
            header: "",
            align: "end",
            render: (row) => {
              if (!rotatable || row.status !== "active") {
                return null;
              }
              return (
                <div className="esm-actions">
                  {/* A plain Button because it writes nothing: it opens the
                      form below. The write inside that form goes through the
                      reason prompt like every other write here. */}
                  <Button
                    variant="secondary"
                    onClick={() => setRotating(rotating === row.id ? null : row.id)}
                  >
                    {rotating === row.id ? "Cancel rotation" : "Rotate"}
                  </Button>
                  <ReasonButton
                    variant="danger"
                    busy={revoke.isPending && revoke.variables?.credentialID === row.id}
                    mutation={{
                      title: `Revoke ${row.label || "this credential"}`,
                      description: (
                        <p>
                          The key ending <code>{row.last_four}</code> stops working for this
                          platform immediately. It is not deleted at the provider — revoke it
                          there too, or it remains a live key somebody else holds.
                        </p>
                      ),
                      confirmLabel: "Revoke credential",
                      run: async (reason) => {
                        const result = await revoke.mutateAsync({
                          credentialID: row.id,
                          reason,
                          accountID: accountId,
                        });
                        onDone(
                          `Credential ••••${result.credential.last_four} is now ${result.credential.status}.`,
                        );
                      },
                    }}
                  >
                    Revoke
                  </ReasonButton>
                </div>
              );
            },
          },
        ]}
      />
      {target ? (
        // Keyed by credential id so switching rows mounts a fresh form: the
        // secret lives in that component's state, and remounting is what
        // guarantees nothing is carried from one credential to another.
        <RotateForm
          key={target.id}
          accountId={accountId}
          credential={target}
          onDone={(message) => {
            setRotating(null);
            onDone(message);
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * The rotation form.
 *
 * THE SECURITY RULE OF THIS SCREEN: the new upstream key is typed into a
 * `type="password"` input, held in this component's local state, and sent as
 * the body of one POST. On success the state is cleared immediately and the
 * mutation is reset. The key is never rendered back, never written to the
 * query cache, never logged, and never placed in a URL — the response carries
 * only safe metadata (last four, fingerprint, timestamps) and that is all
 * this screen has to show.
 */
function RotateForm({
  accountId,
  credential,
  onDone,
}: {
  accountId: string;
  credential: ProviderCredential;
  onDone: (message: string) => void;
}) {
  const rotate = useRotateProviderCredential();
  const [secret, setSecret] = useState("");
  const ready = secret.trim() !== "";

  return (
    <div className="esm-subform">
      <h3>Rotate {credential.label || "this credential"}</h3>
      <p className="esm-muted-text">
        Paste the key you have already created in the provider's own console. The platform stores
        it encrypted, replaces the key ending <code>{credential.last_four}</code>, and answers with
        the new last four and fingerprint so you can check you pasted the right one.
      </p>
      <ErrorAlert error={rotate.error} title="That rotation did not go through" />
      <Field
        label="New upstream key"
        htmlFor="rotate-secret"
        hint="It is sent once, in the body of a single request, and is never shown again."
      >
        <Input
          id="rotate-secret"
          type="password"
          // Kept out of the browser's password manager and out of spell
          // check: neither of them is a place an upstream API key belongs.
          autoComplete="off"
          spellCheck={false}
          autoFocus
          value={secret}
          onChange={(event) => setSecret(event.target.value)}
        />
      </Field>
      <div className="esm-actions">
        <ReasonButton
          variant="danger"
          busy={rotate.isPending}
          disabled={!ready}
          mutation={{
            title: `Rotate ${credential.label || "credential"} on ${credential.provider}`,
            description: (
              <p>
                The key ending <code>{credential.last_four}</code> is replaced by the one you
                pasted. Anything still using the old key upstream stops working, so revoke it at
                the provider once traffic has moved.
              </p>
            ),
            confirmLabel: "Rotate credential",
            run: async (reason) => {
              const result = await rotate.mutateAsync({
                credentialID: credential.id,
                secret,
                reason,
                accountID: accountId,
              });
              // Clear the plaintext before anything else, then drop the
              // mutation state: TanStack keeps a mutation's variables after
              // it settles, and the secret was one of them.
              setSecret("");
              rotate.reset();
              onDone(
                `Rotated to a key ending ${result.credential.last_four} (fingerprint ${result.credential.fingerprint.slice(0, FINGERPRINT_CHARS)}).`,
              );
            },
          }}
        >
          Rotate credential
        </ReasonButton>
      </div>
    </div>
  );
}

function ProfilesCard({ profiles }: { profiles: ProviderProfileRef[] }) {
  return (
    <Card title="Routing profiles using this account">
      {/* The blast radius of suspending the account, in table form: these are
          the applications whose traffic has to go somewhere else. */}
      <DataTable<ProviderProfileRef>
        rows={profiles}
        rowKey={(row) => row.id}
        empty="No routing profile draws on this account, so nothing routes here today."
        columns={[
          {
            key: "profile",
            header: "Profile",
            render: (row) => (
              <Link to="/routing-profiles/$profileId" params={{ profileId: row.id }}>
                {row.name}
              </Link>
            ),
          },
          { key: "slug", header: "Slug", render: (row) => <code>{row.slug}</code> },
          { key: "application", header: "Application", render: (row) => row.application_name },
          {
            key: "status",
            header: "Status",
            render: (row) => <StatusBadge status={row.status} />,
          },
          {
            key: "enabled",
            header: "In the chain",
            render: (row) => <FlagBadge on={row.enabled} label="entry" />,
          },
          { key: "priority", header: "Priority", align: "end", render: (row) => row.priority },
          { key: "weight", header: "Weight", align: "end", render: (row) => row.weight },
        ]}
      />
    </Card>
  );
}
